import { ApiError } from '@api/api-client';
import {
  type EditionResponse,
  type ImportWinnerRow,
  importWinners,
  type ImportWinnersResult,
  updateEdition,
} from '@api/editions-api';
import Modal from '@components/modal';
import { downloadCsv } from '@utils/csv-utils';
import {
  buildFailedWinnerRowsCsv,
  getWinnerPreviews,
  parseWinnerRows,
  validateWinnerHeaders,
  type WinnerPreview,
} from '@utils/csv-winners-parser';
import {
  AlertTriangle,
  CheckCircle,
  Download,
  FileUp,
  Lock,
  Sparkles,
  Trophy,
  Upload,
  X,
} from 'lucide-react';
import { useCallback, useRef, useState } from 'react';
import { sileo } from 'sileo';

interface FinalizeEditionModalProps {
  edition: EditionResponse | null;
  isOpen: boolean;
  onClose: () => void;
  onFinalized: () => void;
}

type Phase = 'choice' | 'idle' | 'preview' | 'importing' | 'done' | 'confirm-close' | 'closing';

export default function FinalizeEditionModal({
  edition,
  isOpen,
  onClose,
  onFinalized,
}: FinalizeEditionModalProps): React.JSX.Element {
  const [phase, setPhase] = useState<Phase>('choice');
  const [fileName, setFileName] = useState('');
  const [parsedRows, setParsedRows] = useState<ImportWinnerRow[]>([]);
  const [previews, setPreviews] = useState<WinnerPreview[]>([]);
  const [result, setResult] = useState<ImportWinnersResult | null>(null);
  const [isDragging, setIsDragging] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);

  // A PMS edition identifies its winners by HubSpot id; a marketplace one by
  // Docplanner doctor id. The server enforces it either way.
  const winnerKeyColumn = edition?.authMode === 'password' ? 'hsid' : 'dp_doctor_id';

  const reset = useCallback((): void => {
    setPhase('choice');
    setFileName('');
    setParsedRows([]);
    setPreviews([]);
    setResult(null);
    setIsDragging(false);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }, []);

  const handleClose = (): void => {
    reset();
    onClose();
  };

  const processFile = useCallback((file: File): void => {
    if (!file.name.endsWith('.csv') && !file.name.endsWith('.tsv') && !file.name.endsWith('.txt')) {
      sileo.error({ title: 'Invalid file type. Please upload a CSV/TSV file.' });
      return;
    }

    const reader = new FileReader();
    reader.onload = (e) => {
      const content = e.target?.result as string;

      const validationError = validateWinnerHeaders(content);
      if (validationError) {
        sileo.error({ title: validationError });
        return;
      }

      const rows = parseWinnerRows(content);
      if (rows.length === 0) {
        sileo.error({ title: 'No valid data rows found in the file.' });
        return;
      }

      setParsedRows(rows);
      setPreviews(getWinnerPreviews(rows));
      setFileName(file.name);
      setPhase('preview');
    };
    reader.readAsText(file);
  }, []);

  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>): void => {
    const file = e.target.files?.[0];
    if (file) processFile(file);
  };

  const handleDragOver = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(true);
  };

  const handleDragLeave = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
  };

  const handleDrop = (e: React.DragEvent): void => {
    e.preventDefault();
    setIsDragging(false);
    const file = e.dataTransfer.files[0];
    if (file) processFile(file);
  };

  const handleImport = async (): Promise<void> => {
    if (!edition) return;
    setPhase('importing');
    try {
      const importResult = await importWinners(edition.id, parsedRows);
      setResult(importResult);
      setPhase('done');

      if (importResult.failedRows.length === 0) {
        sileo.success({
          title: `Edition finalized: ${importResult.winnersMarked} winner(s) marked`,
        });
        onFinalized();
      } else {
        sileo.warning({
          title: `Import rejected: ${importResult.failedRows.length} invalid row(s)`,
        });
      }
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Import failed';
      sileo.error({ title: message });
      setPhase('preview');
    }
  };

  const handleCloseWithoutWinners = async (): Promise<void> => {
    if (!edition) return;
    setPhase('closing');
    try {
      await updateEdition(edition.id, { status: 'closed' });
      sileo.success({ title: 'Edition closed without winners' });
      onFinalized();
      handleClose();
    } catch (error) {
      const message = error instanceof ApiError ? error.message : 'Failed to close edition';
      sileo.error({ title: message });
      setPhase('confirm-close');
    }
  };

  const totalCategories = previews.length;

  const renderChoice = (): React.JSX.Element => (
    <div className="finalize-choice">
      <button type="button" className="finalize-choice__card" onClick={() => setPhase('idle')}>
        <Trophy size={32} className="finalize-choice__icon" />
        <span className="finalize-choice__title">Insertar ganadores manualmente</span>
        <span className="finalize-choice__subtitle">Importa un CSV con los ganadores</span>
      </button>
      <button
        type="button"
        className="finalize-choice__card finalize-choice__card--disabled"
        disabled
      >
        <Sparkles size={32} className="finalize-choice__icon" />
        <span className="finalize-choice__title">Calcular ganadores</span>
        <span className="finalize-choice__subtitle">Próximamente</span>
      </button>
      <button
        type="button"
        className="finalize-choice__card"
        onClick={() => setPhase('confirm-close')}
      >
        <Lock size={32} className="finalize-choice__icon" />
        <span className="finalize-choice__title">Cerrar sin ganadores</span>
        <span className="finalize-choice__subtitle">
          Cierra la edición sin importar ni modificar ganadores
        </span>
      </button>
    </div>
  );

  const renderConfirmClose = (): React.JSX.Element => (
    <div className="finalize-confirm-close">
      <AlertTriangle size={32} className="finalize-confirm-close__icon" />
      <p className="finalize-confirm-close__text">
        La edición <strong>{edition?.name ?? ''}</strong> se cerrará y la votación quedará
        deshabilitada. No se importarán ni modificarán ganadores. Podrás reabrirla más adelante.
      </p>
    </div>
  );

  const renderIdle = (): React.JSX.Element => (
    <div
      className={`csv-drop-zone${isDragging ? ' csv-drop-zone--active' : ''}`}
      onDragOver={handleDragOver}
      onDragLeave={handleDragLeave}
      onDrop={handleDrop}
      onClick={() => fileInputRef.current?.click()}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') fileInputRef.current?.click();
      }}
    >
      <Upload size={40} className="csv-drop-zone__icon" />
      <p className="csv-drop-zone__title">Drag & drop your winners CSV here</p>
      <p className="csv-drop-zone__subtitle">or click to browse. Supported: .csv, .tsv, .txt</p>
      <p className="csv-drop-zone__hint">
        {/* The key column depends on the edition's site, so name the right one
            rather than making them find out from a failed import. */}
        Columns: category_external_id, {winnerKeyColumn}, position (1/2/3)
      </p>
      <input
        ref={fileInputRef}
        type="file"
        accept=".csv,.tsv,.txt"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />
    </div>
  );

  const renderPreview = (): React.JSX.Element => (
    <div className="csv-preview">
      <div className="csv-file-info">
        <FileUp size={20} className="csv-file-info__icon" />
        <div className="csv-file-info__details">
          <p className="csv-file-info__name">{fileName}</p>
          <p className="csv-file-info__meta">
            {totalCategories} categor{totalCategories !== 1 ? 'ies' : 'y'} &middot;{' '}
            {parsedRows.length} winner{parsedRows.length !== 1 ? 's' : ''}
          </p>
        </div>
        <button
          type="button"
          className="btn btn-ghost btn-icon btn-sm"
          onClick={() => setPhase('idle')}
          aria-label="Remove file"
        >
          <X size={16} />
        </button>
      </div>

      <div className="csv-preview__table-wrapper">
        <table className="csv-preview__table">
          <thead>
            <tr>
              <th>Category (external id)</th>
              <th>Winners</th>
            </tr>
          </thead>
          <tbody>
            {previews.map((preview) => (
              <tr key={preview.categoryExternalId}>
                <td>{preview.categoryExternalId}</td>
                <td>{preview.winnerCount}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );

  const renderDone = (): React.JSX.Element | null => {
    if (!result) return null;
    const hasErrors = result.failedRows.length > 0;

    return (
      <div className="csv-result">
        <div
          className={`csv-result__banner csv-result__banner--${hasErrors ? 'warning' : 'success'}`}
        >
          {hasErrors ? (
            <AlertTriangle size={24} className="csv-result__banner-icon--warning" />
          ) : (
            <CheckCircle size={24} className="csv-result__banner-icon--success" />
          )}
          <p className="csv-result__banner-title">
            {hasErrors
              ? 'Import rejected — fix the CSV and try again'
              : 'Edition finalized successfully'}
          </p>
        </div>

        {!hasErrors && (
          <div className="csv-result__stats">
            <div className="csv-result__stat">
              <p className="csv-result__stat-value csv-result__stat-value--primary">
                {result.winnersMarked}
              </p>
              <p className="csv-result__stat-label">Winners marked</p>
            </div>
          </div>
        )}

        {result.errors.length > 0 && (
          <div className="csv-result__warnings">
            <p className="csv-result__warnings-title">Errors:</p>
            <ul className="csv-result__warnings-list">
              {result.errors.map((err) => (
                <li key={err}>{err}</li>
              ))}
            </ul>
          </div>
        )}

        {result.failedRows.length > 0 && (
          <button
            type="button"
            className="btn btn-secondary"
            style={{ alignSelf: 'flex-start' }}
            onClick={() => {
              const csv = buildFailedWinnerRowsCsv(result.failedRows);
              downloadCsv(csv, 'failed-winner-rows.csv');
            }}
          >
            <Download size={16} />
            Download failed rows ({result.failedRows.length})
          </button>
        )}
      </div>
    );
  };

  const title =
    phase === 'choice'
      ? `Finalize "${edition?.name ?? ''}"`
      : phase === 'done'
        ? 'Finalize results'
        : phase === 'preview'
          ? 'Review winners'
          : phase === 'confirm-close' || phase === 'closing'
            ? 'Close without winners'
            : 'Import winners from CSV';

  const footer =
    phase === 'choice' ? (
      <button type="button" className="btn btn-secondary" onClick={handleClose}>
        Cancel
      </button>
    ) : phase === 'done' ? (
      <button type="button" className="btn btn-primary" onClick={handleClose}>
        Close
      </button>
    ) : phase === 'preview' ? (
      <>
        <button type="button" className="btn btn-secondary" onClick={() => setPhase('idle')}>
          Back
        </button>
        <button type="button" className="btn btn-primary" onClick={() => void handleImport()}>
          <Upload size={16} />
          Finalize with {parsedRows.length} winner{parsedRows.length !== 1 ? 's' : ''}
        </button>
      </>
    ) : phase === 'idle' ? (
      <button type="button" className="btn btn-secondary" onClick={() => setPhase('choice')}>
        Back
      </button>
    ) : phase === 'confirm-close' ? (
      <>
        <button type="button" className="btn btn-secondary" onClick={() => setPhase('choice')}>
          Back
        </button>
        <button
          type="button"
          className="btn btn-primary"
          onClick={() => void handleCloseWithoutWinners()}
        >
          <Lock size={16} />
          Close edition
        </button>
      </>
    ) : phase === 'closing' ? (
      <button type="button" className="btn btn-secondary" disabled>
        Closing...
      </button>
    ) : (
      <button type="button" className="btn btn-secondary" onClick={handleClose}>
        Cancel
      </button>
    );

  return (
    <Modal isOpen={isOpen} onClose={handleClose} title={title} footer={footer}>
      {phase === 'choice' && renderChoice()}
      {phase === 'idle' && renderIdle()}
      {phase === 'preview' && renderPreview()}
      {phase === 'confirm-close' && renderConfirmClose()}
      {phase === 'importing' && (
        <div className="csv-importing">
          <p className="csv-importing__title">Finalizing...</p>
          <p className="csv-importing__subtitle">Marking winners and closing the edition</p>
        </div>
      )}
      {phase === 'closing' && (
        <div className="csv-importing">
          <p className="csv-importing__title">Closing...</p>
          <p className="csv-importing__subtitle">Closing the edition without winners</p>
        </div>
      )}
      {phase === 'done' && renderDone()}
    </Modal>
  );
}
